import {
  ICurrencyConstants,
  ICurrencyFormat,
  IDisplayByType,
  IFileDataType,
  ISelectOption,
} from './types';

export const MIN_WIDTH = '1135px';

export const L1Suffix = 'L1Code';
export const L2Suffix = 'L2Code';

export const FILTER_LABELS: {
  [key: string]: string;
} = {
  spendTypes: 'Spend Type',
  contributions: 'Contribution',
  expenseTypes: 'Expense Type',
  serviceTowers: 'Service Towers',
  geographies: 'Geography',
};

export const BREAKDOWN_BY = {
  EXPENSE_TYPE: 'expenseTypesBreakdown',
  CONTRIBUTION: 'contributionsBreakdown',
  GA: 'spendTypesBreakdown',
};

export const EXPENSE_TYPES = {
  CAPEX: 'Capex',
  OPEX: 'Opex',
};

export const CONTRIBUTIONS = {
  THIRD_PARTY: 'Third party',
  PERSONNEL: 'Personnel',
};

export const HEADER_CURRENCIES_TEXT: ICurrencyConstants = {
  USD: 'US$',
  GBP: '£',
  EUR: '€',
};

export const CURRENCIES_SIGNS: ICurrencyConstants = {
  USD: '$',
  GBP: '£',
  EUR: '€',
};

export const UNITS_SIGNS: ICurrencyConstants = {
  Thousands: 'K',
  Millions: 'M',
  Billions: 'B',
};

export const IDEAL_UNITS_SIGNS: ICurrencyConstants = {
  1000: 'K',
  1000000: 'M',
  1000000000: 'B',
  1: '',
};

export const EXPORT_OPTIONS = [
  {
    label: 'Export as image',
    value: 'png',
  },
  {
    label: 'Export as data',
    value: 'xlsx',
  },
];

export const DECIMALS_FORMATS: ICurrencyConstants = {
  None: '0',
  '.0': '0[.]0',
  '.00': '0[.]00',
  '.000': '0[.]000',
  '.0000': '0[.]0000',
};

export const UNITS_DIVIDERS: { [key: string]: number } = {
  Thousands: 1000,
  Millions: 1000000,
  Billions: 1000000000,
};

export const SPEND_BY_TO_API = {
  ServiceTower: 'serviceTowers',
  ServiceTowerL2: 'serviceTowerL2',
  Vendor: 'vendor',
  Geography: 'geographies',
};

export const DEFAULT_CURRENCY_FORMAT: ICurrencyFormat = {
  currency: 'USD',
  decimal: 'None',
  unit: 'Millions',
};

export const SPEND_BY_OPTIONS: ISelectOption[] = [
  {
    label: 'Service Tower',
    value: 'ServiceTower',
  },
  {
    label: 'Vendors',
    value: 'Vendor',
  },
  {
    label: 'Geography',
    value: 'Geography',
  },
];

export const getSpendContributionOptions = (isPersonnelDisabled?: boolean) => [
  {
    label: 'All Spend',
    value: '',
    disabled: Boolean(isPersonnelDisabled),
  },
  {
    label: CONTRIBUTIONS.THIRD_PARTY,
    value: CONTRIBUTIONS.THIRD_PARTY,
  },
  {
    label: CONTRIBUTIONS.PERSONNEL,
    value: CONTRIBUTIONS.PERSONNEL,
    disabled: Boolean(isPersonnelDisabled),
  },
];

export const DISPLAY_BY = {
  COST: 'Spend',
  PERCENTAGE_OF_REVENUE: 'Percentage of revenue',
  HEADCOUNT: 'Headcount',
};

export const DISPLAY_BY_OPTIONS: ISelectOption[] = [
  {
    label: DISPLAY_BY.COST,
    value: DISPLAY_BY.COST,
  },
  {
    label: DISPLAY_BY.PERCENTAGE_OF_REVENUE,
    value: DISPLAY_BY.PERCENTAGE_OF_REVENUE,
  },
  {
    label: DISPLAY_BY.HEADCOUNT,
    value: DISPLAY_BY.HEADCOUNT,
  },
];

export const BREADCRUMBS_MAP = {
  '': 'Technology spend',
  [CONTRIBUTIONS.THIRD_PARTY]: 'Third party spend',
  [CONTRIBUTIONS.PERSONNEL]: 'Personnel spend',
};

export const CHART_TYPES_CONSTANTS = {
  CLUSTERED_COLUMN: 'Clustered Column',
  STACKED_COLUMN: 'Stacked Column',
  STACKED_COLUMN_100: '100% Stacked Column',
  MAP: 'Map',
};

export const CHART_TYPES = [
  {
    label: CHART_TYPES_CONSTANTS.CLUSTERED_COLUMN,
    value: CHART_TYPES_CONSTANTS.CLUSTERED_COLUMN,
    icon: 'stacked_bar_chart',
  },
  {
    label: CHART_TYPES_CONSTANTS.STACKED_COLUMN,
    value: CHART_TYPES_CONSTANTS.STACKED_COLUMN,
    icon: 'stacked_bar_chart',
  },
  {
    label: CHART_TYPES_CONSTANTS.STACKED_COLUMN_100,
    value: CHART_TYPES_CONSTANTS.STACKED_COLUMN_100,
    icon: 'stacked_bar_chart',
  },
  {
    label: CHART_TYPES_CONSTANTS.MAP,
    value: CHART_TYPES_CONSTANTS.MAP,
    icon: 'public',
  },
];

export const DATE_RANGE_FORMAT = 'MMM YY';

// TODO handle color pallette
export const CHART_COLOR_PALLETTE = ['#FF600F', '#ADAFB2'];

export const CLASSIFY_SPEND_LINKS: {
  [k in IFileDataType]: string;
} = {
  [IFileDataType.REVENUE]: '', // Won't be shown on UI
  [IFileDataType.THIRD_PARTY_SPEND]: '/classification/thirdParty',
  [IFileDataType.PERSONNEL_SPEND]: '/classification/personnel',
  [IFileDataType.APPLICATIONS]: '/classification/applications',
  [IFileDataType.PRODUCTS]: '/classification/products',
};

export const PERMISSIONS = {
  BENCHMARKS_ANALYSIS_READ: 'Benchmarks.Analysis.Read',
  BENCHMARKS_CALCULATIONS_READ: 'Benchmarks.Calculations.Read',
  BENCHMARK_ANALYSIS_TABLE_READ: 'Benchmarks.Analysis.Table.Read',
  SPEND_ANALYSIS_READ: 'SpendAnalysis.Read',
  PREFERENCES_READ: 'Preferences.Read',
  PREFERENCES_WRITE: 'Preferences.Write',
  VIEW_PERSONNEL: 'SpendAnalysis.Personnel.Data.Read',
  UPLOAD_SPEND_DATA: 'SpendAnalysis.Data.Upload',
  DOWNLOAD_SPEND_DATA: 'SpendAnalysis.Data.Read',
  DELETE_SPEND_DATA: 'SpendAnalysis.Data.Delete',
  SALARY_READ: 'Salary.Read',
  CLASSIFICATION_RULES_READ: 'Settings.Classification.Read',
  BENCHMARK_SETTINGS_READ: 'IndustryBlends.Read',
  BENCHMARK_SETTINGS_WRITE: 'IndustryBlends.Write',
  SETTINGS_CLASSIFICATION_WRITE: 'Settings.Classification.Write',
  UNPUBLISHED_DATA_READ: 'Classification.Read',
  UNPUBLISHED_DATA_WRITE: 'UnpublishedData.Write',
  APPLICATIONS_DATA_ALLOCATE: 'Applications.Data.Allocate',
  APPLICATIONS_PIE_VIEW: 'Applications.Charts.PieView',
  APPLICATIONS_PERSONNEL: 'Applications.Charts.Personnel',
  RUN_AUTOCLASSIFICATION: 'AutoClassification.Run',
  PRODUCTS_DATA_ALLOCATE: 'Products.Data.Allocate',
  PRODUCTS_CHARTS_PERSONNEL: 'Products.Charts.Personnel',
};

export const DATA_PERMISSIONS = [
  PERMISSIONS.UPLOAD_SPEND_DATA,
  PERMISSIONS.DOWNLOAD_SPEND_DATA,
  PERMISSIONS.DELETE_SPEND_DATA,
];

export const SETTINGS_PERMISSIONS = [
  PERMISSIONS.PREFERENCES_READ,
  PERMISSIONS.CLASSIFICATION_RULES_READ,
  PERMISSIONS.BENCHMARK_SETTINGS_READ,
  ...DATA_PERMISSIONS,
];

export const hasSomeOfRequiredPermissions = (
  permissions: string[],
  requiredPermissions: string[]
) => {
  return requiredPermissions.some((item) => permissions.includes(item));
};

export const COST = 'Spend';
export const PERCENTAGE_OF_REVENUE = 'Percentage of revenue';
export const HEADCOUNT = 'Headcount';

export const DISPLAY_BY_TO_SPEND_VIEW_TYPE = {
  [COST]: 'Cost',
  [PERCENTAGE_OF_REVENUE]: 'PercentageOfRevenue',
  [HEADCOUNT]: 'Headcount',
};

export const DISPLAY_BY_TO_API = {
  [IDisplayByType.COST]: 'Cost',
  [IDisplayByType.PERCENTAGE_OF_REVENUE]: 'PercentageOfRevenue',
  [IDisplayByType.HEADCOUNT]: 'Headcount',
};
